import { Component, OnInit } from '@angular/core';
import { CheckSheetAssignmentProjection, Vendor, User } from '@completion/models';
import { Store } from '@ngrx/store';
import {
  LoadProcurementPackages,
  LoadSuppliers,
  AddProcurementPackageToSelection,
  RemoveProcurementPackageFromSelection,
  SetSelectedCheckSheetAssignments,
  LoadPackageSow,
  ClearAvailableCheckSheetAssignments,
} from '@completion/actions';
import { UpdateVendor } from '@completion/actions';
import { ProcurementPackage } from '@completion/models';
import {
  getAvailableCheckSheetAssignments,
  getCurrentProject,
  getProcurementPackages,
  getSelectedProcurementPackages,
} from '@completion/selectors';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-purchase-order',
  templateUrl: './procurement-package.component.html',
  styleUrls: ['./procurement-package.component.scss'],
  standalone: false,
})
export class ProcurementPackageComponent implements OnInit {
  selectedProcurementPackages: ProcurementPackage[] = [];
  procurementPackages: ProcurementPackage[] = [];
  availableCheckSheetAssignments: CheckSheetAssignmentProjection[] = [];
  filteredAvailableCheckSheetAssignments: CheckSheetAssignmentProjection[] = [];
  displayedColumns: string[] = ['select', 'number', 'vendors', 'assignedPersonsCount'];
  expandedPackageIds: number[] = [];
  selectedAssignments: CheckSheetAssignmentProjection[] = [];
  allAssignmentsSelected: boolean = false;
  destroy$: Subject<void> = new Subject<void>();
  filterText: string = '';
  packageFilterText: string = '';
  hideUnassigned: boolean = false; // toggle state
  assignmentColumnFilters: Record<string, Set<string>> = {};

  get filteredProcurementPackages(): ProcurementPackage[] {
    const text = this.packageFilterText.trim().toLowerCase();
    if (!text) return this.procurementPackages;
    return this.procurementPackages.filter((pkg) => {
      const num = (pkg.number || '').toLowerCase();
      const name = (pkg.name || '').toLowerCase();
      return num.includes(text) || name.includes(text);
    });
  }
  constructor(private store: Store) {}

  ngOnInit(): void {
    this.store
      .select(getCurrentProject)
      .pipe(takeUntil(this.destroy$))
      .subscribe((project) => {
        if (project) {
          this.store.dispatch(new LoadProcurementPackages());
          this.store.dispatch(new LoadSuppliers());
        }
      });
    this.store
      .select(getProcurementPackages)
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => {
        if (data) {
          this.procurementPackages = [...data]; // Ensure the array is not frozen
        }
      });

    this.store
      .select(getSelectedProcurementPackages)
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => {
        this.selectedProcurementPackages = data ? [...data] : [];
        if (this.selectedProcurementPackages.length > 0) {
          this.store.dispatch(new LoadPackageSow());
        } else {
          this.store.dispatch(new ClearAvailableCheckSheetAssignments());
        }
      });

    this.store
      .select(getAvailableCheckSheetAssignments)
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => {
        if (data) {
          this.availableCheckSheetAssignments = [...data];
          this.filteredAvailableCheckSheetAssignments = [...data];
          const validIds = new Set(this.availableCheckSheetAssignments.map((a) => a.id));
          const filteredSelection = this.selectedAssignments.filter((a) => validIds.has(a.id));
          if (filteredSelection.length !== this.selectedAssignments.length) {
            this.selectedAssignments = filteredSelection;
            this.store.dispatch(new SetSelectedCheckSheetAssignments(this.selectedAssignments));
          }
          this.applyFilter();
        }
      });
  }

  onSelectPackage(order: ProcurementPackage, isChecked: boolean): void {
    if (isChecked) {
      this.store.dispatch(new AddProcurementPackageToSelection(order));
    } else {
      this.store.dispatch(new RemoveProcurementPackageFromSelection(order));
    }
  }

  onSelectAssignment(assignment: CheckSheetAssignmentProjection, isChecked: boolean): void {
    if (isChecked) {
      if (!this.selectedAssignments.some((selected) => selected.id === assignment.id)) {
        this.selectedAssignments = [...this.selectedAssignments, assignment];
      }
    } else {
      this.selectedAssignments = this.selectedAssignments.filter((selected) => selected.id !== assignment.id);
    }
    this.store.dispatch(new SetSelectedCheckSheetAssignments(this.selectedAssignments));
    this.recalculateAllSelected();
  }

  onSelectAllAssignments(isChecked: boolean): void {
    const visibleAssignments = this.filteredAvailableCheckSheetAssignments;
    const visibleAssignmentIds = new Set(visibleAssignments.map((assignment) => assignment.id));

    if (isChecked) {
      const selectedAssignmentIds = new Set(this.selectedAssignments.map((assignment) => assignment.id));
      this.selectedAssignments = [
        ...this.selectedAssignments,
        ...visibleAssignments.filter((assignment) => !selectedAssignmentIds.has(assignment.id)),
      ];
    } else {
      this.selectedAssignments = this.selectedAssignments.filter((assignment) => !visibleAssignmentIds.has(assignment.id));
    }
    this.store.dispatch(new SetSelectedCheckSheetAssignments(this.selectedAssignments));
    this.recalculateAllSelected();
  }

  isPackageSelected(procurementPackage: ProcurementPackage): boolean {
    return this.selectedProcurementPackages.some((pkg) => pkg.id === procurementPackage.id);
  }

  isAssignmentSelected(assignment: CheckSheetAssignmentProjection): boolean {
    return this.selectedAssignments.some((selected) => selected.id === assignment.id);
  }

  onFilterKeyup(): void {
    this.applyFilter();
  }

  clearFilter(): void {
    this.filterText = '';
    this.applyFilter();
  }

  applyFilter(): void {
    const f = this.filterText.trim().toLowerCase();
    let base = [...this.availableCheckSheetAssignments];
    if (this.hideUnassigned) {
      base = base.filter((a) => {
        const c: any = (a as any).assignedCount;
        return c === 0; // show only unassigned (hide assigned)
      });
    }
    if (!f) {
      this.filteredAvailableCheckSheetAssignments = this.applyColumnFilters(base);
      this.recalculateAllSelected();
      return;
    }
    base = base.filter((a) => {
      const fields: string[] = [
        a.checkSheetNumber,
        a.tagNumber,
        a.companyNumber,
        (a as any).siteNumber || (a as any).sitenUmber,
        a.phaseNumber,
        a.mcPackageNumber,
        (a as any).procurementPackageNumber,
        a.id != null ? String(a.id) : '',
      ]
        .filter((v) => v != null && v !== '')
        .map((v) => v.toString().toLowerCase());
      return fields.some((val) => val.includes(f));
    });
    this.filteredAvailableCheckSheetAssignments = this.applyColumnFilters(base);
    this.recalculateAllSelected();
  }

  onAssignmentColumnFilter(field: string, values: Set<string> | null): void {
    if (values) {
      this.assignmentColumnFilters[field] = values;
    } else {
      delete this.assignmentColumnFilters[field];
    }
    this.applyFilter();
  }

  private applyColumnFilters(assignments: CheckSheetAssignmentProjection[]): CheckSheetAssignmentProjection[] {
    return assignments.filter((assignment) =>
      Object.entries(this.assignmentColumnFilters).every(([field, values]) => values.has(String((assignment as any)[field] ?? ''))),
    );
  }

  private recalculateAllSelected(): void {
    const filtered = this.filteredAvailableCheckSheetAssignments;
    const selected = this.selectedAssignments;
    if (!filtered?.length) {
      this.allAssignmentsSelected = false;
      return;
    }
    const filteredIds = new Set(filtered.map((a) => a.id));
    const selectedInFiltered = selected.filter((a) => filteredIds.has(a.id));
    this.allAssignmentsSelected = selectedInFiltered.length === filtered.length;
  }

  toggleHideUnassigned(): void {
    this.hideUnassigned = !this.hideUnassigned;
    this.applyFilter();
  }

  getAssignedCountPillClass(a: CheckSheetAssignmentProjection): string {
    const c: any = (a as any).assignedCount;
    if (c == null) return 'none';
    if (c === 1) return 'single';
    if (c > 1) return 'multiple';
    return 'none';
  }

  getAssignedCountLabel(a: CheckSheetAssignmentProjection): string {
    const c: any = (a as any).assignedCount;
    if (c == null) return 'No assignments';
    if (c === 1) return '1 user assigned';
    return `${c} users assigned`;
  }

  getPackageAssignedPillClass(pkg: any): string {
    const c = Number((pkg as any)?.assignedPersonsCount ?? 0);
    return c > 0 ? 'ok' : 'warn';
  }

  getPackageAssignedIcon(pkg: any): string {
    const c = Number((pkg as any)?.assignedPersonsCount ?? 0);
    return c > 0 ? 'group' : 'warning';
  }

  getPackageAssignedLabel(pkg: any): string {
    const c = Number((pkg as any)?.assignedPersonsCount ?? 0);
    return String(c);
  }
  onVendorUserAdded(event: { vendor: Vendor; user: User }): void {
    const { vendor, user } = event;

    // find the procurement package that contains this vendor
    const pkgIndex = this.procurementPackages.findIndex((p) => p.connectedVendors && p.connectedVendors.some((v) => v.id === vendor.id));
    if (pkgIndex === -1) {
      return; // vendor not found in currently loaded packages
    }

    const pkg = this.procurementPackages[pkgIndex];

    const updatedVendors = (pkg.connectedVendors || []).map((v) => {
      if (v.id !== vendor.id) return v;
      const existingUsers = Array.isArray(v.users) ? [...v.users] : [];
      if (!existingUsers.some((u) => u.id === user.id)) {
        existingUsers.push(user);
      }
      return { ...v, users: existingUsers, connectedUsersCount: existingUsers.length } as any;
    });

    // Find the updated vendor to dispatch
    const updatedVendor = updatedVendors.find((v) => v.id === vendor.id);

    // Recalculate assignedPersonsCount from vendor users to keep accurate
    const recalculatedAssignedCount = updatedVendors.reduce(
      (acc, v) => acc + (Array.isArray(v.users) ? v.users.length : v.connectedUsersCount || 0 || 0),
      0,
    );
    const updatedPackage = { ...pkg, connectedVendors: updatedVendors, assignedPersonsCount: recalculatedAssignedCount } as any;

    // update the local array immutably so Angular's change detection updates the table
    this.procurementPackages = this.procurementPackages.map((p, idx) => (idx === pkgIndex ? updatedPackage : p));

    // Persist change by dispatching UpdateVendor for the updated vendor
    if (updatedVendor) {
      // Ensure the action payload matches existing UpdateVendor expectations (full Vendor object)
      this.store.dispatch(new UpdateVendor(updatedVendor));
    }
  }

  onVendorUsersChanged(event: { vendor: Vendor; users: User[] }): void {
    const { vendor, users } = event;

    const pkgIndex = this.procurementPackages.findIndex((p) => p.connectedVendors && p.connectedVendors.some((v) => v.id === vendor.id));
    if (pkgIndex === -1) return;

    const pkg = this.procurementPackages[pkgIndex];

    const updatedVendors = (pkg.connectedVendors || []).map((v) => {
      if (v.id !== vendor.id) return v;
      return { ...v, users: [...users], connectedUsersCount: users.length } as any;
    });

    const updatedVendor = updatedVendors.find((v) => v.id === vendor.id);

    const recalculatedAssignedCount = updatedVendors.reduce(
      (acc, v) => acc + (Array.isArray(v.users) ? v.users.length : v.connectedUsersCount || 0),
      0,
    );
    const updatedPackage = { ...pkg, connectedVendors: updatedVendors, assignedPersonsCount: recalculatedAssignedCount } as any;

    this.procurementPackages = this.procurementPackages.map((p, idx) => (idx === pkgIndex ? updatedPackage : p));

    if (updatedVendor) {
      this.store.dispatch(new UpdateVendor(updatedVendor));
    }
  }
}
